import React, { Suspense } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom';

import { CircularProgress, Box } from '@mui/material';
import RootLayout from '../layouts/RootLayot';
import AccessDenied from '../pages/AccessDenied/AccessDenied';
import { Home, NotFound } from '../pages';
import { paths } from './paths';

// Lightweight fallback for lazy routes
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress size={32} sx={{ color: '#FF564F' }} />
  </Box>
);

// Lazy-loaded pages — each gets its own chunk
const DFMPage = React.lazy(() => import('../pages/DFMPage/DFMPage'));
const CrawlerInbox = React.lazy(() => import('../pages/CrawlerInbox/CrawlerInbox'));
const ValidationWrapper = React.lazy(() => import('../pages/ValidationPage/ValidationWrapper'));
const ValidationV2Wrapper = React.lazy(() => import('../pages/ValidationPageV2/ValidationV2Wrapper'));
const EvaluationPage = React.lazy(() => import('../pages/Evaluation/EvaluationPage'));
const SearchPage = React.lazy(() => import('../pages/Search/SearchPage'));
const BenchmarkingOverview = React.lazy(() => import('../pages/Benchmarking/BenchmarkingOverview'));
const BuyBuild = React.lazy(() => import('../pages/BuyBuild/BuyBuildPage'));
const TechAssessmentPage = React.lazy(() => import('../pages/TechAssessment/TechAssessmentPage'));
const CompanyFilesPage = React.lazy(() => import('../pages/CompanyFiles/CompanyFilesPage'));
const CompanyAnnotationsPage = React.lazy(() => import('../pages/CompanyAnnotations/CompanyAnnotationsPage'));
const AIUploadPage = React.lazy(() => import('../pages/AIUpload/AIUploadPage'));
const SoftwareCategoriesPage = React.lazy(() => import('../pages/SoftwareCategories/SoftwareCategoriesPage'));
const CustomerIndustriesPage = React.lazy(() => import('../pages/CustomerIndustries/CustomerIndustriesPage'));
const MatrixSearchPage = React.lazy(() => import('../pages/MatrixSearch/MatrixSearchPage'));
const NotificationsPage = React.lazy(() => import('../pages/Notifications/NotificationsPage'));
const PipedriveActivityPage = React.lazy(() => import('../pages/PipedriveActivity/PipedriveActivityPage'));
const MLStatsPage = React.lazy(() => import('../pages/MLStats/MLStatsPage'));
const SignalTrackingPage = React.lazy(() => import('../pages/SignalTracking/SignalTrackingPage'));
const UserActivityPage = React.lazy(() => import('../pages/UserActivity/UserActivityPage'));
const CrawlerDashboard = React.lazy(() => import('../pages/CrawlerDashboard/CrawlerDashboard'));
const EnrichmentPerformancePage = React.lazy(() => import('../pages/EnrichmentPerformance/EnrichmentPerformancePage'));
const SWClassifierPage = React.lazy(() => import('../pages/SWClassifier/SWClassifierPage'));
const CrawlQueuePage = React.lazy(() => import('../pages/CrawlQueue/CrawlQueuePage'));
const ValuationModelPage = React.lazy(() => import('../pages/ValuationModel/ValuationModelPage'));
const ScheduledTasksPage = React.lazy(() => import('../pages/ScheduledTasks/ScheduledTasksPage'));
const SettingsPage = React.lazy(() => import('../pages/Settings/SettingsPage'));
const CustomerSegmentPage = React.lazy(() => import('../pages/CustomerSegment/CustomerSegmentPage'));
const IndustryBenchmarksPage = React.lazy(() => import('../pages/IndustryBenchmarks/IndustryBenchmarksPage'));
const MarketSegmentPage = React.lazy(() => import('../pages/MarketSegments/MarketSegmentPage'));
const MarketResearchPage = React.lazy(() => import('../pages/MarketResearch/MarketResearchPage'));
const AIThreatIndexPage = React.lazy(() => import('../pages/AIThreatIndex/AIThreatIndexPage'));
const StartupsPage = React.lazy(() => import('../pages/Startups/StartupsPage'));
const DealAnnouncementsPage = React.lazy(() => import('../pages/DealAnnouncements/DealAnnouncementsPage'));
const TransactionsPage = React.lazy(() => import('../pages/Transactions/TransactionsPage'));
const ITSupportPage = React.lazy(() => import('../pages/ITSupport/ITSupport'));
const InvestorsGraphPage = React.lazy(() => import('../pages/InvestorsGraph/InvestorsGraphPage'));
const AIThreatDesignPage = React.lazy(() => import('../pages/AIThreatDesign/AIThreatDesignPage'));
const AppInsightPage = React.lazy(() => import('../pages/AppInsight/AppInsightPage'));
const UpcomingDeals = React.lazy(() => import('../components/UpcomingDeals/UpcomingDeals'));
const MeetingsPage = React.lazy(() => import('../pages/MeetingsPage/MeetingsPage'));
const SurveyPage = React.lazy(() => import('../pages/SurveyPage/SurveyPage'));
const QuestionnaireAdmin = React.lazy(() => import('../pages/SurveyPage/QuestionnaireAdmin'));
const OriginationPipelinePage = React.lazy(() => import('../pages/OriginationPipeline/OriginationPipelinePage'));

// Wrap lazy component in Suspense
const L = ({ children }: { children: React.ReactNode }) => <Suspense fallback={<PageLoader />}>{children}</Suspense>;

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/access-denied" element={<AccessDenied />} />

      <Route path="/" element={<RootLayout />}>
        <Route index element={<Home />} />
        <Route
          path={paths.search}
          element={
            <L>
              <SearchPage />
            </L>
          }
        />
        <Route
          path={paths.validation}
          element={
            <L>
              <ValidationWrapper />
            </L>
          }
        />
        <Route
          path={paths.validationV2}
          element={
            <L>
              <ValidationV2Wrapper />
            </L>
          }
        />
        <Route
          path={paths.evaluation.evaluation}
          element={
            <L>
              <EvaluationPage />
            </L>
          }
        />
        <Route
          path={paths.evaluation.dfm}
          element={
            <L>
              <DFMPage />
            </L>
          }
        />
        <Route
          path={paths.evaluation.techAssessment}
          element={
            <L>
              <TechAssessmentPage />
            </L>
          }
        />
        <Route
          path={paths.evaluation.companyFiles}
          element={
            <L>
              <CompanyFilesPage />
            </L>
          }
        />
        <Route
          path={paths.evaluation.companyAnnotations}
          element={
            <L>
              <CompanyAnnotationsPage />
            </L>
          }
        />
        <Route
          path={paths.evaluation.bbLists}
          element={
            <L>
              <BuyBuild />
            </L>
          }
        />
        <Route path={paths.evaluation.outreach} element={<div>Outreach Page</div>} />
        <Route
          path={paths.crawlerInbox}
          element={
            <L>
              <CrawlerInbox />
            </L>
          }
        />
        <Route
          path={paths.benchmarking}
          element={
            <L>
              <BenchmarkingOverview />
            </L>
          }
        />
        <Route
          path={paths.aiUpload}
          element={
            <L>
              <AIUploadPage />
            </L>
          }
        />
        <Route
          path={paths.companyFinder.softwareCategories}
          element={
            <L>
              <SoftwareCategoriesPage />
            </L>
          }
        />
        <Route
          path={paths.companyFinder.customerIndustries}
          element={
            <L>
              <CustomerIndustriesPage />
            </L>
          }
        />
        <Route
          path={paths.companyFinder.matrixSearch}
          element={
            <L>
              <MatrixSearchPage />
            </L>
          }
        />
        <Route
          path={paths.evaluation.marketResearch}
          element={
            <L>
              <MarketResearchPage />
            </L>
          }
        />
        <Route
          path={paths.evaluation.aiThreatIndex}
          element={
            <L>
              <AIThreatIndexPage />
            </L>
          }
        />
        <Route
          path={paths.evaluation.startups}
          element={
            <L>
              <StartupsPage />
            </L>
          }
        />
        <Route
          path={paths.notifications}
          element={
            <L>
              <NotificationsPage />
            </L>
          }
        />
        <Route
          path={paths.pipedriveActivity}
          element={
            <L>
              <PipedriveActivityPage />
            </L>
          }
        />
        <Route path={paths.crawlerOps.main} element={<div>Crawler OPs Page</div>} />
        <Route
          path={paths.crawlerOps.pipedrive}
          element={
            <L>
              <UpcomingDeals />
            </L>
          }
        />
        <Route
          path={paths.crawlerOps.mlStats}
          element={
            <L>
              <MLStatsPage />
            </L>
          }
        />
        <Route
          path={paths.crawlerOps.signalTracking}
          element={
            <L>
              <SignalTrackingPage />
            </L>
          }
        />
        <Route
          path={paths.crawlerOps.userActivity}
          element={
            <L>
              <UserActivityPage />
            </L>
          }
        />
        <Route
          path={paths.crawlerOps.dashboard}
          element={
            <L>
              <CrawlerDashboard />
            </L>
          }
        />
        <Route
          path={paths.crawlerOps.enrichmentPerformance}
          element={
            <L>
              <EnrichmentPerformancePage />
            </L>
          }
        />
        <Route
          path={paths.crawlerOps.swClassifier}
          element={
            <L>
              <SWClassifierPage />
            </L>
          }
        />
        <Route
          path={paths.crawlerOps.crawlQueue}
          element={
            <L>
              <CrawlQueuePage />
            </L>
          }
        />
        <Route
          path={paths.research.valuationModel}
          element={
            <L>
              <ValuationModelPage />
            </L>
          }
        />
        <Route
          path={paths.statistics}
          element={
            <L>
              <OriginationPipelinePage />
            </L>
          }
        />
        <Route
          path={paths.evaluation.customerSegment}
          element={
            <L>
              <CustomerSegmentPage />
            </L>
          }
        />
        <Route
          path={paths.settings}
          element={
            <L>
              <SettingsPage />
            </L>
          }
        />
        <Route path={paths.publicAnalysis} element={<div>Public Analysis Page</div>} />
        <Route
          path={paths.evaluation.marketSegments}
          element={
            <L>
              <MarketSegmentPage companyId={''} />
            </L>
          }
        />
        <Route
          path={paths.dealAnnouncements}
          element={
            <L>
              <DealAnnouncementsPage />
            </L>
          }
        />
        <Route
          path={paths.transactions}
          element={
            <L>
              <TransactionsPage />
            </L>
          }
        />
        <Route
          path={paths.industryBenchmarks}
          element={
            <L>
              <IndustryBenchmarksPage />
            </L>
          }
        />
        <Route
          path={paths.research.industryBenchmarks}
          element={
            <L>
              <IndustryBenchmarksPage />
            </L>
          }
        />
        <Route
          path={paths.itSupport}
          element={
            <L>
              <ITSupportPage />
            </L>
          }
        />
        <Route
          path={paths.investorsGraph}
          element={
            <L>
              <InvestorsGraphPage />
            </L>
          }
        />
        <Route
          path={paths.aiThreatDesign}
          element={
            <L>
              <AIThreatDesignPage />
            </L>
          }
        />
        <Route
          path={paths.scheduledTasks}
          element={
            <L>
              <ScheduledTasksPage />
            </L>
          }
        />
        <Route
          path={paths.appInsight}
          element={
            <L>
              <AppInsightPage />
            </L>
          }
        />

        <Route
          path={paths.meetings}
          element={
            <L>
              <MeetingsPage />
            </L>
          }
        />
        <Route
          path={paths.survey}
          element={
            <L>
              <SurveyPage />
            </L>
          }
        />
        <Route
          path={paths.admin.questionnaires}
          element={
            <L>
              <QuestionnaireAdmin />
            </L>
          }
        />
        <Route path="/*" element={<NotFound />} />
      </Route>
    </>
  )
);

export default router;
